import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  X,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Sparkles,
  Images,
  Upload,
  Plus,
  Trash2,
  Loader2,
  Image as ImageIcon,
  Tag,
  Type,
  Code2,
  FileText,
  UserCheck,
  Lightbulb,
} from "lucide-react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";
import styles from "./Projects.module.css";

const ColorAttributor = Quill.import("attributors/style/color");
const BackgroundAttributor = Quill.import("attributors/style/background");
const SizeAttributor = Quill.import("attributors/style/size");

const fontSizeWhitelist = [
  "4px",
  "6px",
  "8px",
  "9px",
  "10px",
  "11px",
  "12px",
  "13px",
  "14px",
  "15px",
  "16px",
  "18px",
  "20px",
  "22px",
  "24px",
  "26px",
  "28px",
  "32px",
];
SizeAttributor.whitelist = fontSizeWhitelist;

Quill.register(ColorAttributor, true);
Quill.register(BackgroundAttributor, true);
Quill.register(SizeAttributor, true);
Quill.register("modules/imageResize", ImageResize);

/**
 * Check if a string or HTML string has real text content
 */
function hasContent(str) {
  if (!str) return false;
  const stripped = str
    .replace(/<figure[^>]*>[\s\S]*?<\/figure>/gi, "")
    .replace(/<img[^>]*>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .trim();
  return stripped.length > 0;
}

/**
 * Clean description by removing inline <img> & <figure> tags
 */
function cleanHtml(html) {
  if (!html) return "";
  return html
    .replace(/<figure[^>]*>[\s\S]*?<\/figure>/gi, "")
    .replace(/<img[^>]*>/gi, "")
    .trim();
}

/**
 * Parse skills text/HTML into a clean list of skill pills
 */
function parseSkillItems(skillsContent) {
  if (!skillsContent) return [];
  const text = skillsContent
    .replace(/<li[^>]*>/gi, "\n")
    .replace(/<p[^>]*>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&");

  return text
    .split(/[\n,•·\r]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && s.length < 70);
}

/**
 * Extracts gallery image list from project data
 */
function extractGalleryData(project) {
  let imagesList = [];

  if (Array.isArray(project?.images) && project.images.length > 0) {
    imagesList = [...project.images];
  } else if (project?.description) {
    const matches = [
      ...project.description.matchAll(/<img[^>]+src="([^">]+)"/g),
    ];
    imagesList = matches.map((m) => m[1]);
  }

  return { imagesList };
}

export default function ProjectDetail({
  project,
  editable = false,
  onUpdateProject,
  onBack,
}) {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(project?.title || "");
  const [type, setType] = useState(
    project?.type && ["App", "Data", "AI"].includes(project.type)
      ? project.type
      : project?.type
      ? "Custom"
      : "App"
  );
  const [customType, setCustomType] = useState(
    project?.type && !["App", "Data", "AI"].includes(project.type)
      ? project.type
      : ""
  );
  const [isCustomType, setIsCustomType] = useState(
    project?.type ? !["App", "Data", "AI"].includes(project.type) : false
  );
  const [description, setDescription] = useState(project?.description || "");
  const [keySkills, setKeySkills] = useState(project?.keySkills || "");
  const [overview, setOverview] = useState(project?.overview || "");
  const [myContribution, setMyContribution] = useState(project?.myContribution || "");
  const [challengesSolutions, setChallengesSolutions] = useState(project?.challengesSolutions || "");

  const [date, setDate] = useState(
    project?.date ? project.date.split("T")[0] : ""
  );
  const [coverImage, setCoverImage] = useState(project?.coverImage || "");
  const [images, setImages] = useState(project?.images || []);
  const [lightboxIndex, setLightboxIndex] = useState(null); // number or null
  const [uploading, setUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);

  const fileInputRef = useRef(null);
  const coverFileInputRef = useRef(null);
  const API_BASE = process.env.REACT_APP_API_BASE || (typeof window !== "undefined" && window.location?.hostname ? `http://${window.location.hostname}:3001` : "http://localhost:3001");

  useEffect(() => {
    if (project) {
      setTitle(project.title || "");
      const pType = project.type || "App";
      if (["App", "Data", "AI"].includes(pType)) {
        setType(pType);
        setIsCustomType(false);
        setCustomType("");
      } else {
        setType("Custom");
        setIsCustomType(true);
        setCustomType(pType);
      }
      const rawDesc = project.description || "";
      setDescription(rawDesc);
      setKeySkills(project.keySkills || "");
      
      // Overview fallback: if project has legacy description and new fields are empty, use description as overview
      const hasNewFields = project.overview || project.keySkills || project.myContribution || project.challengesSolutions;
      const initialOverview = project.overview || (!hasNewFields && rawDesc ? rawDesc : "");
      setOverview(initialOverview);
      
      setMyContribution(project.myContribution || "");
      setChallengesSolutions(project.challengesSolutions || "");
      setDate(project.date ? project.date.split("T")[0] : "");
      setCoverImage(project.coverImage || "");

      const { imagesList } = extractGalleryData(project);
      setImages(imagesList);
    }
  }, [project]);

  // Display overview fallback
  const displayOverview = overview || (!keySkills && !myContribution && !challengesSolutions ? description : "");

  const displayImages = useMemo(() => (images.length > 0 ? images : []), [images]);

  // Lightbox Navigation Handlers
  const handlePrevImage = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setLightboxIndex((prev) => {
        if (prev === null) return null;
        return (prev - 1 + displayImages.length) % displayImages.length;
      });
    },
    [displayImages.length]
  );

  const handleNextImage = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setLightboxIndex((prev) => {
        if (prev === null) return null;
        return (prev + 1) % displayImages.length;
      });
    },
    [displayImages.length]
  );

  // Keyboard navigation for Lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowLeft") handlePrevImage();
      if (e.key === "ArrowRight") handleNextImage();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, handlePrevImage, handleNextImage]);

  // Gallery sticky positioning: when scrolled to the bottom of the artworks, keep it fixed
  const leftTextRef = useRef(null);
  const galleryRef = useRef(null);
  const [galleryStickyTop, setGalleryStickyTop] = useState(null);

  useEffect(() => {
    let rafId = null;
    const updateSticky = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (!galleryRef.current) return;
        if (window.innerWidth <= 900) {
          setGalleryStickyTop(null);
          return;
        }
        const galleryH = galleryRef.current.scrollHeight || galleryRef.current.offsetHeight;
        const leftH = leftTextRef.current ? (leftTextRef.current.scrollHeight || leftTextRef.current.offsetHeight) : 0;
        if (!galleryH) return;

        // If the left text content is shorter than or equal to the right gallery (e.g. Elim Church project with 17 images),
        // the gallery is the taller content and should scroll naturally to the bottom without sticking mid-scroll.
        if (leftH > 0 && leftH <= galleryH) {
          setGalleryStickyTop(null);
          return;
        }

        const vh = window.innerHeight;
        const topMargin = 56; // 3.5rem (just below sticky top bar)
        const bottomMargin = 24; // 1.5rem bottom padding
        
        // When gallery is taller than the viewport space, set a negative top offset
        // so it scrolls upwards with the page until its bottom edge is visible, then sticks.
        // When gallery is shorter, stick at topMargin.
        if (galleryH > vh - topMargin - bottomMargin) {
          const bottomAlignedTop = vh - galleryH - bottomMargin;
          setGalleryStickyTop(`${bottomAlignedTop}px`);
        } else {
          setGalleryStickyTop(`${topMargin}px`);
        }
      });
    };

    updateSticky();

    // Re-check after images load to ensure precise height
    const t1 = setTimeout(updateSticky, 100);
    const t2 = setTimeout(updateSticky, 400);
    const t3 = setTimeout(updateSticky, 1000);
    const t4 = setTimeout(updateSticky, 2000);

    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateSticky) : null;
    if (ro) {
      if (galleryRef.current) ro.observe(galleryRef.current);
      if (leftTextRef.current) ro.observe(leftTextRef.current);
    }

    const imgElements = galleryRef.current?.querySelectorAll("img");
    imgElements?.forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", updateSticky);
        img.addEventListener("error", updateSticky);
      }
    });

    window.addEventListener("resize", updateSticky);
    window.addEventListener("scroll", updateSticky, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      if (ro) ro.disconnect();
      imgElements?.forEach((img) => {
        img.removeEventListener("load", updateSticky);
        img.removeEventListener("error", updateSticky);
      });
      window.removeEventListener("resize", updateSticky);
      window.removeEventListener("scroll", updateSticky);
    };
  }, [displayImages]);

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ size: fontSizeWhitelist }],
      ["bold", "italic", "underline", "blockquote"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "clean"],
    ],
  };

  const formats = [
    "header",
    "size",
    "bold",
    "italic",
    "underline",
    "blockquote",
    "list",
    "bullet",
    "link",
    "color",
    "background",
  ];

  // Cover image upload handler
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCoverUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("type", "project");
      formData.append("title", (title.trim() || "untitled") + "-cover");

      const res = await fetch(`${API_BASE}/api/upload`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (data.url) {
        setCoverImage(data.url);
      }
    } catch (err) {
      alert("❌ Cover upload failed: " + err.message);
    } finally {
      setCoverUploading(false);
      if (coverFileInputRef.current) coverFileInputRef.current.value = "";
    }
  };

  // Gallery image file upload handler
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("type", "project");
        formData.append("title", title.trim() || "untitled");

        const res = await fetch(`${API_BASE}/api/upload`, {
          method: "POST",
          body: formData,
        });

        const data = await res.json();
        if (data.url) {
          uploadedUrls.push(data.url);
        }
      }

      if (uploadedUrls.length > 0) {
        setImages((prev) => [...prev, ...uploadedUrls]);
      }
    } catch (err) {
      alert("❌ Upload failed: " + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleMoveImage = (idx, direction) => {
    setImages((prev) => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[idx];
      next[idx] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  const handleDeleteImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  // Save changes in Admin mode
  const handleSave = async () => {
    try {
      const finalType = isCustomType ? customType.trim() || "App" : type;
      const res = await fetch(`${API_BASE}/api/projects/${project._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          type: finalType,
          description: overview || description,
          keySkills,
          overview,
          myContribution,
          challengesSolutions,
          date,
          coverImage,
          images,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to update project");
      
      // Update local state directly
      setTitle(data.title || "");
      const pType = data.type || "App";
      if (["App", "Data", "AI"].includes(pType)) {
        setType(pType);
        setIsCustomType(false);
        setCustomType("");
      } else {
        setType("Custom");
        setIsCustomType(true);
        setCustomType(pType);
      }
      setKeySkills(data.keySkills || "");
      setOverview(data.overview || "");
      setMyContribution(data.myContribution || "");
      setChallengesSolutions(data.challengesSolutions || "");
      setDescription(data.description || "");
      if (data.date) setDate(data.date.split("T")[0]);
      setCoverImage(data.coverImage || "");
      if (Array.isArray(data.images)) setImages(data.images);

      onUpdateProject?.(data);
      setIsEditing(false);
      alert("✅ Project updated successfully!");
    } catch (err) {
      alert("❌ " + err.message);
    }
  };

  const handleBackClick = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      navigate("/projects");
    }
  }, [onBack, navigate]);

  const formattedDate = project?.date
    ? new Date(project.date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
    : "Curated Exhibition";

  const skillItems = parseSkillItems(keySkills);

  // ── ADMIN EDITABLE MODE ──
  if (editable) {
    return (
      <div className="w-full max-w-6xl mx-auto px-6 sm:px-10 py-5 bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200/70 dark:border-gray-800 text-xs">
        <div className="flex justify-between items-center mb-3 pb-2.5 border-b border-gray-100 dark:border-gray-800">
          <div className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
            <Sparkles size={16} className="text-[var(--primary)]" />
            Curator Project Editor
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className="text-[11px] font-semibold text-[var(--primary)] hover:underline"
            >
              ← Back to List
            </button>
          )}
        </div>

        <div className="flex flex-col gap-3.5">
          {/* Title, Type & Date */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5">
            <div className="md:col-span-2">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
                <Type size={12} className="text-[var(--primary)]" />
                Project Title
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full p-2 rounded-lg text-xs border dark:bg-gray-800 dark:text-gray-100"
                />
              ) : (
                <h3 className="text-sm font-bold text-[var(--primary)]">{title}</h3>
              )}
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
                <Tag size={12} className="text-[var(--primary)]" />
                Project Type
              </label>
              {isEditing ? (
                <div className="flex flex-col gap-1">
                  <select
                    value={isCustomType ? "Custom" : type}
                    onChange={(e) => {
                      if (e.target.value === "Custom") {
                        setIsCustomType(true);
                        setType("Custom");
                      } else {
                        setIsCustomType(false);
                        setType(e.target.value);
                      }
                    }}
                    className="w-full p-2 rounded-lg text-xs border dark:bg-gray-800 dark:text-gray-100 font-medium"
                  >
                    <option value="App">App</option>
                    <option value="Data">Data</option>
                    <option value="AI">AI</option>
                    <option value="Custom">Custom (自定义)...</option>
                  </select>
                  {isCustomType && (
                    <input
                      type="text"
                      placeholder="Enter custom type..."
                      value={customType}
                      onChange={(e) => setCustomType(e.target.value)}
                      className="w-full p-1 text-xs rounded border border-green-500 dark:bg-gray-800 dark:text-gray-100"
                      required
                    />
                  )}
                </div>
              ) : (
                <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 dark:bg-green-950/60 text-green-700 dark:text-green-300">
                  {project?.type || "App"}
                </span>
              )}
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
                <Calendar size={12} className="text-[var(--primary)]" />
                Exhibition Date
              </label>
              {isEditing ? (
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full p-2 rounded-lg text-xs border dark:bg-gray-800 dark:text-gray-100"
                />
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  {date || "No date set"}
                </p>
              )}
            </div>
          </div>

          {/* Edit / View Toggle Header */}
          <div className="flex justify-between items-center pt-1 border-t border-gray-100 dark:border-gray-800">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
              Structured Project Content (4 Sections)
            </span>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-3 py-1 bg-[var(--primary)] text-white font-semibold rounded-md hover:opacity-90 transition text-[11px]"
              >
                Edit Project
              </button>
            )}
          </div>

          {/* 1. KEY SKILLS */}
          <div className="p-3 bg-gray-50/70 dark:bg-gray-800/60 rounded-xl border border-gray-200/60 dark:border-gray-700 flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                <Code2 size={13} className="text-[var(--primary)]" />
                1. Key Skills (主要技术与技能)
              </label>
              <span className="text-[10px] text-gray-400 italic">Technologies, frameworks, tools</span>
            </div>

            {isEditing ? (
              <div>
                <div className={styles.resizableEditorContainer}>
                  <ReactQuill
                    value={keySkills}
                    onChange={setKeySkills}
                    theme="snow"
                    modules={modules}
                    formats={formats}
                    placeholder="e.g. Next.js, React, TypeScript, Tailwind CSS, Node.js, Express, MongoDB, Cloudinary, JWT, REST API..."
                  />
                </div>
                <div className={styles.editorResizeHint}>
                  <span>Supports multiline text, bullets, or tags</span>
                  <span className={styles.editorResizeBadge}>↕ Drag bottom corner to resize</span>
                </div>
              </div>
            ) : (
              <div className="p-2.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
                {hasContent(keySkills) ? (
                  skillItems.length > 0 ? (
                    <div className={styles.skillsTagsWrap}>
                      {skillItems.map((skill, idx) => (
                        <span key={idx} className={styles.skillPill}>
                          {skill}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div
                      className="prose dark:prose-invert max-w-none text-xs"
                      dangerouslySetInnerHTML={{ __html: cleanHtml(keySkills) }}
                    />
                  )
                ) : (
                  <p className="text-gray-400 italic text-[11px]">No key skills specified.</p>
                )}
              </div>
            )}
          </div>

          {/* 2. OVERVIEW */}
          <div className="p-3 bg-gray-50/70 dark:bg-gray-800/60 rounded-xl border border-gray-200/60 dark:border-gray-700 flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                <FileText size={13} className="text-[var(--primary)]" />
                2. Overview (项目概述)
              </label>
              <span className="text-[10px] text-gray-400 italic">Concise summary, purpose, problem solved</span>
            </div>

            {isEditing ? (
              <div>
                <div className={styles.resizableEditorContainer}>
                  <ReactQuill
                    value={overview}
                    onChange={setOverview}
                    theme="snow"
                    modules={modules}
                    formats={formats}
                    placeholder="Provide a concise explanation of what the project is, why it was created, and what problem it solves..."
                  />
                </div>
                <div className={styles.editorResizeHint}>
                  <span>Supports rich text formatting and paragraphs</span>
                  <span className={styles.editorResizeBadge}>↕ Drag bottom corner to resize</span>
                </div>
              </div>
            ) : (
              <div className="p-2.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
                {hasContent(displayOverview) ? (
                  <div
                    className="prose dark:prose-invert max-w-none text-xs"
                    dangerouslySetInnerHTML={{ __html: cleanHtml(displayOverview) }}
                  />
                ) : (
                  <p className="text-gray-400 italic text-[11px]">No overview provided.</p>
                )}
              </div>
            )}
          </div>

          {/* 3. MY CONTRIBUTION */}
          <div className="p-3 bg-gray-50/70 dark:bg-gray-800/60 rounded-xl border border-gray-200/60 dark:border-gray-700 flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                <UserCheck size={13} className="text-[var(--primary)]" />
                3. My Contribution (个人职责与贡献)
              </label>
              <span className="text-[10px] text-gray-400 italic">Personal achievements & responsibilities</span>
            </div>

            {isEditing ? (
              <div>
                <div className={styles.resizableEditorContainer}>
                  <ReactQuill
                    value={myContribution}
                    onChange={setMyContribution}
                    theme="snow"
                    modules={modules}
                    formats={formats}
                    placeholder="Explain what you personally designed, developed, implemented, or were responsible for..."
                  />
                </div>
                <div className={styles.editorResizeHint}>
                  <span>Supports bullet points, lists, and formatted items</span>
                  <span className={styles.editorResizeBadge}>↕ Drag bottom corner to resize</span>
                </div>
              </div>
            ) : (
              <div className="p-2.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
                {hasContent(myContribution) ? (
                  <div
                    className="prose dark:prose-invert max-w-none text-xs"
                    dangerouslySetInnerHTML={{ __html: cleanHtml(myContribution) }}
                  />
                ) : (
                  <p className="text-gray-400 italic text-[11px]">No contribution notes added.</p>
                )}
              </div>
            )}
          </div>

          {/* 4. CHALLENGES & SOLUTIONS */}
          <div className="p-3 bg-gray-50/70 dark:bg-gray-800/60 rounded-xl border border-gray-200/60 dark:border-gray-700 flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                <Lightbulb size={13} className="text-[var(--primary)]" />
                4. Challenges & Solutions (挑战与解决方案)
              </label>
              <span className="text-[10px] text-gray-400 italic">Technical problem solving demonstrations</span>
            </div>

            {isEditing ? (
              <div>
                <div className={styles.resizableEditorContainer}>
                  <ReactQuill
                    value={challengesSolutions}
                    onChange={setChallengesSolutions}
                    theme="snow"
                    modules={modules}
                    formats={formats}
                    placeholder="Challenge: ... &#10;Solution: ... &#10;&#10;Challenge: ... &#10;Solution: ..."
                  />
                </div>
                <div className={styles.editorResizeHint}>
                  <span>Demonstrates problem solving abilities</span>
                  <span className={styles.editorResizeBadge}>↕ Drag bottom corner to resize</span>
                </div>
              </div>
            ) : (
              <div className="p-2.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
                {hasContent(challengesSolutions) ? (
                  <div
                    className="prose dark:prose-invert max-w-none text-xs"
                    dangerouslySetInnerHTML={{ __html: cleanHtml(challengesSolutions) }}
                  />
                ) : (
                  <p className="text-gray-400 italic text-[11px]">No challenges & solutions added.</p>
                )}
              </div>
            )}
          </div>

          {/* Cover Image Upload Section (紧凑并排) */}
          <div className="p-3 bg-gray-50/70 dark:bg-gray-800/60 rounded-xl border border-gray-200/60 dark:border-gray-700 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300 flex items-center gap-1">
                <Sparkles size={12} className="text-[var(--primary)]" />
                Cover Image (封面图片)
              </label>
              <span className="text-[10px] text-gray-400 italic">
                Exhibition main cover
              </span>
            </div>

            <div className="flex items-center gap-3">
              {isEditing && (
                <div
                  onClick={() => coverFileInputRef.current?.click()}
                  className="w-40 h-24 border-2 border-dashed border-green-600/30 hover:border-[var(--primary)] rounded-xl bg-green-50/20 dark:bg-gray-800/40 flex flex-col items-center justify-center p-1.5 text-center cursor-pointer transition hover:bg-green-50/50 flex-shrink-0"
                >
                  <input
                    ref={coverFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCoverUpload}
                    className="hidden"
                  />
                  {coverUploading ? (
                    <Loader2 size={16} className="animate-spin text-[var(--primary)] mb-0.5" />
                  ) : (
                    <Upload size={16} className="text-[var(--primary)] mb-0.5" />
                  )}
                  <span className="font-semibold text-[11px] text-gray-700 dark:text-gray-200">
                    {coverUploading ? "Uploading..." : "Upload Cover"}
                  </span>
                  <span className="text-[9px] text-gray-400">JPG, PNG, WebP</span>
                </div>
              )}

              {coverImage ? (
                <div className="relative w-40 h-24 rounded-xl overflow-hidden border border-green-400 bg-gray-50 flex-shrink-0 shadow-sm group">
                  <img
                    src={coverImage}
                    alt="Cover"
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-1 left-1 bg-[var(--primary)] text-white text-[9px] font-bold px-1 rounded shadow">
                    Cover
                  </span>
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => setCoverImage("")}
                      className="absolute top-1 right-1 bg-red-600/80 hover:bg-red-600 text-white p-0.5 rounded transition opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
              ) : (
                <div className="w-40 h-24 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 flex flex-col items-center justify-center text-gray-400 flex-shrink-0 text-[10px]">
                  <ImageIcon size={16} className="opacity-40 mb-0.5" />
                  <span>No cover set</span>
                </div>
              )}
            </div>
          </div>

          {/* Dedicated Image Gallery (6列网格 + 首位上传插槽) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                  <Images size={13} className="text-[var(--primary)]" />
                  Attached Gallery Images ({images.length})
                </label>
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="px-2 py-0.5 text-[10px] font-bold bg-green-100 dark:bg-green-950/60 text-green-700 dark:text-green-300 rounded hover:bg-green-200 transition flex items-center gap-1"
                  >
                    {uploading ? (
                      <Loader2 size={10} className="animate-spin" />
                    ) : (
                      <Plus size={10} />
                    )}
                    <span>Upload Artworks</span>
                  </button>
                )}
              </div>
            </div>

            {isEditing && (
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
            )}

            <div className={styles.adminPreviewGrid}>
              {/* Interactive Upload Tile Slot in Edit Mode */}
              {isEditing && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={styles.adminUploadTile}
                  title="Click to select image files"
                >
                  {uploading ? (
                    <Loader2 size={16} className="animate-spin text-[var(--primary)]" />
                  ) : (
                    <Plus size={18} className="text-[var(--primary)]" />
                  )}
                  <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300">
                    {uploading ? "Uploading..." : "Add Images"}
                  </span>
                </div>
              )}

              {/* Uploaded Gallery Thumbnails */}
              {images.map((imgUrl, idx) => (
                <div key={idx} className={styles.adminThumbItem}>
                  <img
                    src={imgUrl}
                    alt={`Preview ${idx + 1}`}
                    className={styles.adminThumbImg}
                  />
                  <span className="absolute top-1 left-1 bg-black/60 text-white text-[9px] font-bold px-1 rounded">
                    #{idx + 1}
                  </span>

                  {isEditing && (
                    <div className={styles.adminThumbActions}>
                      {idx > 0 && (
                        <button
                          type="button"
                          onClick={() => handleMoveImage(idx, -1)}
                          className={styles.adminThumbBtn}
                        >
                          <ChevronLeft size={12} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteImage(idx)}
                        className={`${styles.adminThumbBtn} ${styles.adminThumbBtnDanger}`}
                      >
                        <Trash2 size={12} />
                      </button>
                      {idx < images.length - 1 && (
                        <button
                          type="button"
                          onClick={() => handleMoveImage(idx, 1)}
                          className={styles.adminThumbBtn}
                        >
                          <ChevronRight size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!isEditing && images.length === 0 && (
              <p className="text-gray-400 italic text-[11px] mt-1">No images attached yet.</p>
            )}
          </div>

          {isEditing && (
            <div className="flex gap-2 justify-start mt-2 pt-2 border-t">
              <button
                onClick={handleSave}
                className="bg-green-600 text-white text-xs font-bold px-3.5 py-1.5 rounded-lg hover:bg-green-700 transition"
              >
                Save Changes
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="bg-gray-200 text-gray-700 text-xs font-semibold px-3.5 py-1.5 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── PUBLIC DIGITAL ART GALLERY VIEW ──
  const hasAnyStructuredSection =
    hasContent(keySkills) ||
    hasContent(displayOverview) ||
    hasContent(myContribution) ||
    hasContent(challengesSolutions);

  return (
    <div className={styles.detailPage}>
      {/* Sticky Top Bar with Back Button */}
      <div className={styles.detailTopBar}>
        <button
          onClick={handleBackClick}
          className={styles.backBtn}
          type="button"
          aria-label="Back to Exhibition Gallery"
        >
          <ArrowLeft size={13} />
          <span>Back to Gallery</span>
        </button>
      </div>

      {/* Two-Column Body: A4 Text Left + Exhibition Artworks Right */}
      <div className={styles.detailBody}>
        {/* Left: A4-style Text Panel */}
        <div ref={leftTextRef} className={styles.detailTextSection}>
          <div className={styles.detailHeaderMeta}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
              <h1 className={styles.detailTitle}>{project?.title}</h1>
              {project?.type && (
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-green-100 dark:bg-green-950/70 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-800 flex-shrink-0">
                  {project.type}
                </span>
              )}
            </div>
            <p className={styles.detailDate}>
              <Calendar size={12} />
              <span>{formattedDate}</span>
            </p>
          </div>

          {hasAnyStructuredSection ? (
            <div className={styles.detailSectionsContainer}>
              {/* 1. KEY SKILLS */}
              {hasContent(keySkills) && (
                <div className={styles.detailSection}>
                  <div className={styles.detailSectionHeader}>
                    <div className={styles.detailSectionHeadingLeft}>
                      <Code2 size={14} className={styles.detailSectionIcon} />
                      <h2 className={styles.detailSectionTitle}>Key Skills</h2>
                    </div>
                  </div>
                  {skillItems.length > 0 ? (
                    <div className={styles.skillsTagsWrap}>
                      {skillItems.map((skill, idx) => (
                        <span key={idx} className={styles.skillPill}>
                          {skill}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div
                      className={styles.detailSectionContent}
                      dangerouslySetInnerHTML={{ __html: cleanHtml(keySkills) }}
                    />
                  )}
                </div>
              )}

              {/* 2. OVERVIEW */}
              {hasContent(displayOverview) && (
                <div className={styles.detailSection}>
                  <div className={styles.detailSectionHeader}>
                    <div className={styles.detailSectionHeadingLeft}>
                      <FileText size={14} className={styles.detailSectionIcon} />
                      <h2 className={styles.detailSectionTitle}>Overview</h2>
                    </div>
                  </div>
                  <div
                    className={styles.detailSectionContent}
                    dangerouslySetInnerHTML={{ __html: cleanHtml(displayOverview) }}
                  />
                </div>
              )}

              {/* 3. MY CONTRIBUTION */}
              {hasContent(myContribution) && (
                <div className={styles.detailSection}>
                  <div className={styles.detailSectionHeader}>
                    <div className={styles.detailSectionHeadingLeft}>
                      <UserCheck size={14} className={styles.detailSectionIcon} />
                      <h2 className={styles.detailSectionTitle}>My Contribution</h2>
                    </div>
                  </div>
                  <div
                    className={styles.detailSectionContent}
                    dangerouslySetInnerHTML={{ __html: cleanHtml(myContribution) }}
                  />
                </div>
              )}

              {/* 4. CHALLENGES & SOLUTIONS */}
              {hasContent(challengesSolutions) && (
                <div className={styles.detailSection}>
                  <div className={styles.detailSectionHeader}>
                    <div className={styles.detailSectionHeadingLeft}>
                      <Lightbulb size={14} className={styles.detailSectionIcon} />
                      <h2 className={styles.detailSectionTitle}>Challenges & Solutions</h2>
                    </div>
                  </div>
                  <div
                    className={styles.detailSectionContent}
                    dangerouslySetInnerHTML={{ __html: cleanHtml(challengesSolutions) }}
                  />
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-400 italic text-xs">
              Exhibition curatorial notes and details.
            </p>
          )}
        </div>

        {/* Right: Exhibition Artworks (2-column grid) */}
        {displayImages.length > 0 && (
          <div
            ref={galleryRef}
            className={styles.gallerySection}
            style={
              galleryStickyTop
                ? { position: "sticky", top: galleryStickyTop }
                : undefined
            }
          >
            <div className={styles.gallerySectionHeader}>
              <h2 className={styles.gallerySectionTitle}>
                <Images size={14} className="text-[var(--primary)]" />
                <span>Exhibition Artworks</span>
              </h2>
              <span className={styles.gallerySectionCount}>
                {displayImages.length} work{displayImages.length !== 1 ? "s" : ""} on display
              </span>
            </div>

            {/* 2-Column Responsive Grid */}
            <div className={styles.sixColGrid}>
              {displayImages.map((imgUrl, idx) => (
                <motion.div
                  key={`gallery-img-${idx}`}
                  whileHover={{ y: -3, scale: 1.03 }}
                  transition={{ duration: 0.2 }}
                  className={styles.galleryThumbCard}
                  onClick={() => setLightboxIndex(idx)}
                  title="Click to view artwork"
                >
                  <img
                    src={imgUrl}
                    alt={`${project?.title || "Artwork"} #${idx + 1}`}
                    className={styles.galleryThumbImg}
                  />
                  <span className={styles.galleryThumbBadge}>#{idx + 1}</span>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>


      {/* Lightbox Modal with Left/Right Sliding Navigation */}
      <AnimatePresence>
        {lightboxIndex !== null && displayImages[lightboxIndex] && (
          <motion.div
            key="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={styles.lightboxOverlay}
            onClick={() => setLightboxIndex(null)}
          >
            <motion.div
              key={`lightbox-img-${lightboxIndex}`}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ type: "spring", damping: 28, stiffness: 340 }}
              className={styles.lightboxContent}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                type="button"
                className={styles.lightboxClose}
                onClick={() => setLightboxIndex(null)}
                aria-label="Close image viewer"
              >
                <X size={16} />
              </button>

              {/* Prev Navigation Button */}
              {displayImages.length > 1 && (
                <button
                  type="button"
                  className={`${styles.lightboxNavBtn} ${styles.lightboxPrev}`}
                  onClick={handlePrevImage}
                  aria-label="Previous image"
                  title="Previous image (Left Arrow)"
                >
                  <ChevronLeft size={22} />
                </button>
              )}

              {/* Active Image */}
              <img
                src={displayImages[lightboxIndex]}
                alt={`Artwork ${lightboxIndex + 1}`}
                className={styles.lightboxImage}
              />

              {/* Next Navigation Button */}
              {displayImages.length > 1 && (
                <button
                  type="button"
                  className={`${styles.lightboxNavBtn} ${styles.lightboxNext}`}
                  onClick={handleNextImage}
                  aria-label="Next image"
                  title="Next image (Right Arrow)"
                >
                  <ChevronRight size={22} />
                </button>
              )}

              {/* Image Counter */}
              <div className={styles.lightboxCounter}>
                <span>
                  {lightboxIndex + 1} / {displayImages.length}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
